import { DataGrid } from '@thingslog/ui-components';
import React, { FC, useState, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { GridToolbar, GridValueGetterParams } from '@mui/x-data-grid';
import { GridRenderCellParams } from '@thingslog/ui-components';
import { format } from 'date-fns';
import { Tooltip, Chip } from '@mui/material';
import { GeneratedReportDto } from '@thingslog/repositories';

const GeneratedScheduledReportsTable: FC<GeneratedScheduledReportsTableProps> = ({
  generatedReportRows,
}: GeneratedScheduledReportsTableProps) => {
  const { t } = useTranslation();
  const [size, setSize] = useState<number>(10);

  const renderCellTooltip = (params: GridRenderCellParams): ReactNode => {
    return (
      <Tooltip key={params.id} title={params.value || ''} arrow>
        <span>{params.value}</span>
      </Tooltip>
    );
  };

  const renderReportType = (params: GridRenderCellParams): ReactNode => {
    const reportType = params.value;
    if (!reportType) return null;

    const typeColors: Record<
      string,
      'primary' | 'secondary' | 'success' | 'error' | 'warning' | 'info'
    > = {
      CONSUMPTION_REPORT: 'primary',
      CONSUMPTION_EXPORT_REPORT: 'primary',
      CUSTOMER_BILLING_REPORT: 'secondary',
      ALARM_REPORT: 'error',
      TRANSMISSION_REPORT: 'info',
      LICENSE_REPORT: 'warning',
    };

    return (
      <Chip
        label={reportType.replace(/_/g, ' ')}
        color={typeColors[reportType] || 'default'}
        size="small"
      />
    );
  };

  return (
    <DataGrid
      rows={generatedReportRows}
      getRowId={(row: GeneratedReportTableRow): number => row.rowId}
      pageSize={size}
      rowsPerPageOptions={[10, 25, 50, 100]}
      onPageSizeChange={(pageSize: number): void => {
        setSize(pageSize);
      }}
      autoHeight
      hideFooterSelectedRowCount
      components={{ Toolbar: GridToolbar }}
      componentsProps={{
        toolbar: {
          csvOptions: { allColumns: true },
        },
      }}
      columns={[
        {
          field: 'reportConfigName',
          headerName: t('generated_reports_config_name_header'),
          flex: 1.5,
          minWidth: 200,
          renderCell: renderCellTooltip,
        },
        {
          field: 'reportDate',
          headerName: t('generated_reports_date_header'),
          flex: 1,
          minWidth: 180,
          type: 'dateTime',
          valueGetter: (params: GridValueGetterParams): string => {
            return format(new Date(params.value), 'dd MMM yyyy HH:mm');
          },
          renderCell: renderCellTooltip,
        },
        {
          field: 'reportType',
          headerName: t('generated_reports_type_header'),
          flex: 1,
          minWidth: 200,
          renderCell: renderReportType,
        },
        {
          field: 'fileName',
          headerName: t('generated_reports_file_name_header'),
          flex: 1.5,
          minWidth: 250,
          renderCell: renderCellTooltip,
        },
        {
          field: 'parameters',
          headerName: t('generated_reports_parameters_header'),
          flex: 1,
          minWidth: 180,
          renderCell: renderCellTooltip,
        },
      ]}
    />
  );
};

interface GeneratedScheduledReportsTableProps {
  generatedReportRows: GeneratedReportTableRow[];
}

type GeneratedReportTableRow = GeneratedReportDto & {
  rowId: number;
};

export default GeneratedScheduledReportsTable;
